// The Workflows route's catalogue read (komatsu.K19 #693), gathered once for
// every surface that shows it: the Workflows page itself and the project
// home's Workflows index row (polish.P23), so the reference and the route read
// one function over one basis. Glue only: it collects the reader's rail,
// `money:view`, the session cases, the K6 investigation runs, the replay frames
// and the Advisory strip's case basis (the rail badge's), and hands them to the
// pure `workflowCatalogueRows` projection.

import { useMemo } from 'react';
import { usePersona } from '@/app/providers';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useAnalysisReplays } from '@/versions/v1/shared/useAnalysisReplay';
import { useAdvisoryFlowInput } from '@/versions/v1/shared/useAdvisoryFlowRead';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { visibleProjectNav } from '@/versions/v1/shared/project-sidebar-nav';
import {
  caseReplayFixtures,
  casesWithSession,
} from '@/versions/v1/modules/advisory/Scene.transform';
import { useInvestigationRunRows } from '@/versions/v1/modules/advisory/useInvestigationRunRows';
import { workflowCatalogueRows, type WorkflowCatalogueRow } from './WorkflowCatalogue.transform';

export function useWorkflowCatalogueRows(projectSlug: string): WorkflowCatalogueRow[] {
  const { state, diagnosis } = useScenario();
  const advisory = useAdvisory();
  const { can } = useRole();
  const showMoney = can('money:view');
  const { persona } = usePersona();
  const railSegments = useMemo(
    () => new Set<string>(visibleProjectNav(persona).items.map((item) => item.segment)),
    [persona],
  );
  const {
    runs: investigationRuns,
    tools: investigationTools,
    ready: investigationReady,
  } = useInvestigationRunRows();
  const replayFixtures = useMemo(() => caseReplayFixtures(CASES), []);
  const framesByFixture = useAnalysisReplays(replayFixtures);
  const framesReady = replayFixtures.every((fixture) => framesByFixture[fixture] !== undefined);
  const cases = useMemo(
    () => casesWithSession(state, advisory, diagnosis),
    [state, advisory, diagnosis],
  );
  const flowInput = useAdvisoryFlowInput();
  const advisoryFlow = useMemo(
    () => (flowInput
      ? {
        cases: flowInput.cases,
        actions: flowInput.actions,
        reviews: flowInput.reviews,
        stageTimelines: flowInput.stageTimelines,
        fmeaLearning: flowInput.fmeaLearning,
      }
      : null),
    [flowInput],
  );
  const projectPath = `/projects/${projectSlug}`;

  return useMemo(
    () => workflowCatalogueRows({
      projectPath,
      cursor: state.cursor,
      cases,
      investigationRuns,
      investigationTools,
      investigationReady,
      framesByFixture,
      framesReady,
      railSegments,
      showMoney,
      advisoryFlow,
    }),
    [projectPath, state.cursor, cases, investigationRuns, investigationTools, investigationReady, framesByFixture, framesReady, railSegments, showMoney, advisoryFlow],
  );
}
